import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';

const PAGE_SIZE = 30;

// Human labels for the content_type keys in summary.by_content_type.
const CT_LABELS = {
  product_title: 'Product titles',
  product_body_html: 'Product descriptions',
  product_meta_title: 'Meta titles',
  product_meta_desc: 'Meta descriptions',
  product_type: 'Product types',
  option_label: 'Option labels',
  option_value: 'Option values',
  metafield_value: 'Metafields',
  metaobject_value: 'Metaobjects',
  collection_field: 'Collections',
  theme_string: 'Theme strings',
  content_resource: 'Pages & articles',
  menu_link: 'Menus',
  filter_label: 'Filter labels',
};

function RunBadge({ status }) {
  const styles = {
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
    running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 animate-pulse',
  };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${styles[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
}

function formatDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return '—';
  const secs = Math.max(0, Math.round((new Date(finishedAt) - new Date(startedAt)) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Trend of the translated-but-unpushed backlog vs the previous run (the next
// row in the DESC-ordered list). Growth means translations are not reaching
// Shopify — the signal that caught the July gid-crash — so it renders red.
function UnpushedCell({ run, prevRun }) {
  const val = run.summary?.unpushed_after;
  if (val == null) return <span className="text-muted-foreground">—</span>;
  const prev = prevRun?.summary?.unpushed_after;
  let icon = null;
  let cls = 'text-muted-foreground';
  if (prev != null) {
    if (val > prev) { icon = <TrendingUp className="h-3 w-3" />; cls = 'text-red-600 dark:text-red-400'; }
    else if (val < prev) { icon = <TrendingDown className="h-3 w-3" />; cls = 'text-emerald-600 dark:text-emerald-400'; }
    else { icon = <Minus className="h-3 w-3" />; }
  }
  return (
    <span className={`inline-flex items-center gap-1 ${cls}`}>
      {val.toLocaleString()} {icon}
    </span>
  );
}

export default function DailyRuns() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      // limit + 1: the extra (oldest) run is never rendered — it only feeds
      // the unpushed-trend comparison for the last visible row, so the trend
      // arrow doesn't blank out at page boundaries.
      const res = await axios.get('api/transly/sync-runs/', {
        params: { sync_type: 'daily_translate', limit: PAGE_SIZE + 1, offset },
      });
      setRuns(res.data.runs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch daily runs:', err);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const formatDate = (s) => (s ? new Date(s).toLocaleString() : '—');
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">Daily runs</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{total.toLocaleString()} runs</span>
          <Button variant="outline" size="sm" onClick={fetchRuns}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        The daily translate pipeline runs at 00:00 / 06:00 / 12:00 / 18:00 (local) — it extracts new
        Shopify content, translates it, and pushes it live. Expand a run to see exactly what went out.
      </p>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Started</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Duration</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Rows pushed</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Products</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Unpushed backlog</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && runs.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No runs recorded yet.</td></tr>
                )}
                {!loading && runs.slice(0, PAGE_SIZE).map((run, i) => {
                  const s = run.summary || {};
                  const isExpanded = expandedId === run.id;
                  const failedSteps = s.failed_steps || [];
                  return (
                    <React.Fragment key={run.id}>
                      <tr
                        className="border-b border-border hover:bg-accent transition-colors cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : run.id)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-foreground">{formatDate(run.started_at)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDuration(run.started_at, run.finished_at)}</td>
                        <td className="px-4 py-3">
                          <RunBadge status={run.status} />
                          {failedSteps.length > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase text-orange-600 dark:text-orange-400">
                              <AlertTriangle className="h-3 w-3" /> {failedSteps.length} failed step{failedSteps.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-foreground">
                          {s.nothing_new ? (
                            // On an error run "nothing new" would read as reassuring
                            // when it actually means "extraction crashed, unknown".
                            <span className="text-muted-foreground">{run.status === 'success' ? 'nothing new' : '—'}</span>
                          ) : (
                            (s.rows_pushed ?? run.new_count ?? 0).toLocaleString()
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-foreground">
                          {s.nothing_new ? '—' : (s.products_touched ?? '—')}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <UnpushedCell run={run} prevRun={runs[i + 1]} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="px-4 py-4 bg-muted/40">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                              {(s.by_content_type || []).length > 0 && (
                                <div>
                                  <div className="font-medium text-muted-foreground mb-1">Pushed by content type</div>
                                  <ul className="space-y-0.5">
                                    {s.by_content_type.map((ct) => (
                                      <li key={ct.content_type} className="flex justify-between max-w-[280px]">
                                        <span className="text-foreground">{CT_LABELS[ct.content_type] || ct.content_type}</span>
                                        <span className="text-muted-foreground">{ct.rows.toLocaleString()} rows ({ct.distinct} distinct)</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {(s.product_names || []).length > 0 && (
                                <div>
                                  <div className="font-medium text-muted-foreground mb-1">
                                    Products touched{s.product_names_total > s.product_names.length ? ` (first ${s.product_names.length} of ${s.product_names_total})` : ''}
                                  </div>
                                  <ul className="space-y-0.5 max-h-56 overflow-auto">
                                    {s.product_names.map((nm) => (
                                      <li key={nm} className="text-foreground">– {nm}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {failedSteps.length > 0 && (
                                <div className="md:col-span-2">
                                  <div className="font-medium text-destructive mb-1">Failed steps</div>
                                  <div className="flex flex-wrap gap-1">
                                    {failedSteps.map((st) => (
                                      <span key={st} className="font-mono bg-destructive/10 text-destructive px-1.5 py-0.5 rounded">{st}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {run.error && (
                                <div className="md:col-span-2">
                                  <div className="font-medium text-destructive mb-1">Error</div>
                                  <pre className="whitespace-pre-wrap break-words bg-destructive/5 p-2 rounded border border-destructive/30 max-h-48 overflow-auto">{run.error}</pre>
                                </div>
                              )}
                              <div className="md:col-span-2 text-muted-foreground">
                                {s.pending_at_start != null && (
                                  <span><span className="font-mono">pending at start:</span> {s.pending_at_start}<span className="mx-3">·</span></span>
                                )}
                                {s.log_file && <span className="font-mono">{s.log_file}</span>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
